'use client';

import { Fragment } from 'react';
import { css } from '@bts-web/utils-style-engine';
import { languageTitleByCode, type Language } from '@bts-web/utils-lokalise';
import { isSupportedLanguage } from '@bts-web/utils-lokalise';
import { DropdownMenuItem } from '@radix-ui/react-dropdown-menu';
import { LanguageChangeMenuItem } from '../LanguageChangeMenuItem/LanguageChangeMenuItem';
import { LanguageTranslations } from '../LanguageMenu/LanguageMenu.use-client';
import { FilledSpinnerIos } from '../../common/svgIcons';
import { getAppConfig } from '@bts-web/core-app-config';

export const LanguageChangeMenu = ({
  translations,
  onLanguageValueChange,
  currentValue,
  isDisabled,
  isWithinDropdown,
  isLoading,
}: {
  translations: LanguageTranslations;
  currentValue: string;
  onLanguageValueChange: (languageValue: string) => void;
  isDisabled?: boolean;
  isWithinDropdown?: boolean;
  isLoading?: boolean;
}) => {
  const languagesISO = getAppConfig().supportedLanguages;

  const labelsByLanguage: Record<Language, string> = {
    de: translations.germanSubtitle,
    en: translations.englishSubtitle,
    ar: translations.arabicSubtitle,
    fr: translations.frenchSubtitle,
  };

  const WrapperElement = isWithinDropdown ? DropdownMenuItem : Fragment;

  return (
    <ul
      key={currentValue}
      className={css({
        position: 'relative',
        maxHeight: '285px',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        ...(isWithinDropdown
          ? {
              borderRadius: '12px',
              boxShadow: `0px -6px 12px 0px rgba(158,158,158,0.20)`,
            }
          : {}),
        '& li': {
          width: '100%',
          ...(!isWithinDropdown
            ? {
                '&:first-of-type': {
                  borderStartStartRadius: '13px',
                  borderStartEndRadius: '13px',
                },
                '&:last-of-type': {
                  borderBottomLeftRadius: '13px',
                  borderBottomRightRadius: '13px',
                },
              }
            : {}),
        },
      })}
    >
      {isLoading && (
        <div
          className={css({
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            insetInline: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bottom: 0,
          })}
          style={{
            backdropFilter: 'blur(4px)',
          }}
          data-testid="language-change-menu-loading"
        >
          <FilledSpinnerIos
            aria-label="loading icon"
            data-element="loading-icon"
            theme="filled"
          />
        </div>
      )}
      {languagesISO
        .filter((language) => isSupportedLanguage(language))
        .map((language) => (
          <li key={language}>
            <WrapperElement>
              <LanguageChangeMenuItem
                onChange={() => {
                  onLanguageValueChange(language as Language);
                }}
                subtitle={labelsByLanguage[language as Language]}
                title={languageTitleByCode[language as Language]}
                active={currentValue === language}
                disabled={currentValue === language || isDisabled}
              />
            </WrapperElement>
          </li>
        ))}
    </ul>
  );
};
